import React, { useState, useEffect } from "react";
import { Github, Linkedin, Menu, X } from "lucide-react";

const RESUME_URL = process.env.PUBLIC_URL + "/Ashwin_Sridharan_AI_Infrastructure_Platform_Resume.pdf";

const navLinks = [
  { id: "about", title: "About" },
  { id: "experience", title: "Experience" },
  { id: "projects", title: "Projects" },
  { id: "blogs", title: "Blogs" },
  { id: "contact", title: "Contact" },
];

function NavLink({ id, title, onClick }) {
  const scrollToSection = () => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    if (onClick) onClick();
  };

  return (
    <button
      onClick={scrollToSection}
      className="cursor-pointer text-gray-300 hover:text-teal-300 transition-colors duration-300 text-sm"
    >
      {title}
    </button>
  );
}

export default function Layout({ children }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToHero = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="text-slate-300 font-sans min-h-screen" style={{ background: '#050a12' }}>

      {/* ── Ambient background glows ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-60 -left-40 w-[700px] h-[700px] rounded-full blur-[140px]"
          style={{ background: 'radial-gradient(circle,rgba(20,184,166,0.1) 0%,transparent 70%)' }} />
        <div className="absolute top-1/2 -right-60 w-[600px] h-[600px] rounded-full blur-[130px]"
          style={{ background: 'radial-gradient(circle,rgba(109,40,217,0.07) 0%,transparent 70%)' }} />
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full blur-[120px]"
          style={{ background: 'radial-gradient(circle,rgba(20,184,166,0.06) 0%,transparent 70%)' }} />
      </div>
      {/* ── Dot grid ── */}
      <div className="fixed inset-0 pointer-events-none -z-10"
        style={{
          backgroundImage: 'radial-gradient(circle,rgba(148,163,184,0.055) 1px,transparent 1px)',
          backgroundSize: '30px 30px',
        }} />

      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "backdrop-blur-xl shadow-xl" : "transparent"
        }`}
        style={isScrolled ? { background: 'rgba(5,10,18,0.85)', borderBottom: '1px solid rgba(15,23,42,0.8)' } : {}}
      >
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold tracking-wider">
            <button onClick={scrollToHero} className="cursor-pointer"
              style={{ background: 'linear-gradient(135deg,#2dd4bf,#5eead4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              AS
            </button>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <span key={link.id} className="text-teal-300 font-mono text-sm">
                <NavLink {...link} />
              </span>
            ))}
            <a
              href={RESUME_URL}
              rel="noopener noreferrer"
              download
              className="ml-4 border border-teal-500/50 text-teal-300 px-4 py-1.5 rounded-md text-sm transition-all duration-200 hover:shadow-[0_0_14px_rgba(45,212,191,0.25)]" style={{ background: 'rgba(45,212,191,0.06)' }}
            >
              Resume
            </a>
          </nav>
          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-teal-300">
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 h-screen backdrop-blur-xl" style={{ background: 'rgba(5,10,18,0.97)' }}>
            <nav className="flex flex-col items-center justify-center h-full space-y-8 text-lg">
              {navLinks.map((link) => (
                <NavLink key={link.id} {...link} onClick={() => setIsMenuOpen(false)} />
              ))}
              <a
                href={RESUME_URL}
                rel="noopener noreferrer"
                download
                className="mt-8 border border-teal-500/50 text-teal-300 px-6 py-2 rounded-md transition-all duration-200" style={{ background: 'rgba(45,212,191,0.06)' }}
              >
                Resume
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Social Links Sidebar */}
      <div className="hidden md:flex flex-col items-center fixed bottom-0 left-10 z-10">
        <a
          href="https://www.linkedin.com/in/ashwin-sridharan-b8474520a"
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-slate-400 hover:text-teal-300 hover:-translate-y-2 transition-all"
        >
          <Linkedin size={20} />
        </a>
        <a
          href="https://github.com/AshwinSridharan0410"
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-slate-400 hover:text-teal-300 hover:-translate-y-2 transition-all"
        >
          <Github size={20} />
        </a>
        <div className="w-px h-24 bg-slate-600 mt-4"></div>
      </div>

      {/* Email Sidebar */}
      <div className="hidden md:flex flex-col items-center fixed bottom-0 right-10 z-10">
        <a
          href="https://mail.google.com/mail/?view=cm&fs=1&to=ashwinsri04@gmail.com"
          target="_blank"
          rel="noopener noreferrer"
          className="writing-mode-vertical-rl text-slate-400 font-mono text-xs tracking-widest hover:text-teal-300 hover:-translate-y-2 transition-all p-2"
          style={{ writingMode: "vertical-rl" }}
        >
          ashwinsri04@gmail.com
        </a>
        <div className="w-px h-24 bg-slate-600 mt-4"></div>
      </div>

      <main>{children}</main>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-8 text-center text-slate-500 font-mono text-xs">
        <div className="md:hidden flex justify-center space-x-6 mb-6">
          <a href="https://www.linkedin.com/in/ashwin-sridharan-b8474520a" target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-teal-300">
            <Linkedin size={24} />
          </a>
          <a href="https://github.com/AshwinSridharan0410" target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-teal-300">
            <Github size={24} />
          </a>
        </div>
        {/* Visitor Counter Badge */}
        <div className="flex justify-center mb-2">
            <img
            src="https://visitor-badge.laobi.icu/badge?page_id=ashwinsridharan0410.portfolio"
            alt="visitor badge"
            className="mx-auto"
            />
        </div>
        <p>Designed & Built by Ashwin Sridharan</p>
        <p>&copy; {new Date().getFullYear()} Ashwin Sridharan. All Rights Reserved.</p>
      </footer>
    </div>
  );
}
